import usersService from '../../../../../../../../../services/usersService';

/**
 * Fetches unique DOT numbers for the user and selected site, including lab/testing authority info.
 *
 * @param {object} selectedSiteId - The object ID of the selected site.
 * @param {string} currentUserId - The current user object ID.
 * @returns {Promise<Array<{ DOTNumber: string, legacyClientId: number, testingAuthorities: Array<{ testingAuthority: string, testingAuthorityId: number }> }>>}
 *   Array of DOT objects with deduped testing authorities.
 * @throws {Error} - Throws an error if the fetch fails.
 */
export async function fetchDotListWithLabData(legacyClientId, currentUserId) {
  try {
    if(!legacyClientId || !currentUserId) {
      return [];
    } 

    const { dotResults, labAccounts } = await usersService.getDotAndLabData(legacyClientId, currentUserId);
    // Map ERID to array of lab accounts
    const labAccountMap = labAccounts.reduce((acc, lab) => {
      if (lab.ERID != null) (acc[lab.ERID] = acc[lab.ERID] || []).push(lab);
      return acc;
    }, {});

    // Only include DOTs with at least one lab account, dedupe authorities by id
    const dotListWithLab = dotResults
      .filter(dot => dot.DOTNumber && labAccountMap[dot.legacyClientId]?.length)
    .map(dot => {
      const allAuthorities = labAccountMap[dot.legacyClientId].flatMap(labAcc => {
        if (Array.isArray(labAcc.testingAuthorities)) {
          return labAcc.testingAuthorities;
        } else if (labAcc.testingAuthority) {
          return [{ testingAuthority: labAcc.testingAuthority, testingAuthorityId: labAcc.testingAuthorityId }];
        } else {
          return [];
        }
      });
      // Deduplicate authorities by id
      const uniqueAuthorities = Array.from(
        new Map(allAuthorities.map(a => [a.testingAuthorityId, a])).values()
      );
      return { ...dot, testingAuthorities: uniqueAuthorities };
    });

    // Deduplicate DOTs by DOTNumber
    return Array.from(
      new Map(dotListWithLab.map(item => [item.DOTNumber.trim(), item])).values()
    );
  } catch (error) {
    console.error('Error fetching DOT list with lab accounts:', error);
    throw error;
  }
}